// TopbarSearchForm migrated from Final Form to react-hook-form + yup.
// This is a live-onChange search filter: there is no submit button. Output
// fires the instant a place is selected (selectedPlace truthy), mirroring the
// original Field.onChange -> onSubmit({ location }) behavior.

import { yupResolver } from '@hookform/resolvers/yup';

import Box from '@mui/material/Box';
import React, { memo, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import * as yup from 'yup';

import ControlledLocationAutocomplete from '../../components/BasicUI/ControlledInputs/ControlledLocationAutocomplete';
import IconSearch from '../../components/Icons/IconSearch';
import useScreenSize from '../../util/hooks/useScreenSize';

// The original Final Form Field had no `validate` prop and rendered no error
// messages, so the location field stays unvalidated here too. The field value
// in form state is the address string the input writes via field.onChange.
const schema = yup.object().shape({
  location: yup.string(),
});

const preventFormSubmit = (e) => e.preventDefault();

/**
 * TopbarSearchForm is a live location search filter used in the topbar / search
 * page. It emits the selected place immediately via onSubmit; there is no
 * submit button.
 *
 * @param {object} props
 * @param {Function} props.onSubmit - called with { location: { search, selectedPlace, locationPlace } } when a place is selected
 * @param {boolean} [props.inSearchPage] - whether the form is rendered inside the search page
 * @param {{ location?: object|null }} [props.initialValues] - initial location value
 */
const TopbarSearchFormComponent = ({ onSubmit, inSearchPage = false, initialValues }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();

  const placeholder = useMemo(
    () => intl.formatMessage({ id: 'TopbarSearchForm.placeholder' }),
    [intl]
  );

  // ControlledLocationAutocomplete stores the address STRING in form state and
  // seeds the visible input from field.value. The old LocationAutocompleteInput
  // rendered initialValues.location.search, so seed the search string here too
  // (passing the raw object would make MUI Autocomplete's inputValue an object
  // and break the initial display).
  const { control } = useForm({
    defaultValues: {
      location: initialValues?.location?.search ?? '',
    },
    resolver: yupResolver(schema),
  });

  // Live filter: fire output the moment a place is selected. clearOnSelect on
  // the input clears/blurs the box after commit, replacing the old explicit
  // searchInputRef.current.blur().
  const handleLocationChange = useCallback(
    (location) => {
      if (location.selectedPlace) {
        onSubmit({ location });
      }
    },
    [onSubmit]
  );

  return (
    <Box>
      <Box component="form" onSubmit={preventFormSubmit}>
        <Box sx={{ alignItems: 'center', display: 'flex', width: '100%' }}>
          {/* Search-page only: a leading magnifier rendered OUTSIDE the input
              (not via MUI startAdornment, which MUI drops/flickers on select). */}
          {inSearchPage ? (
            <Box
              sx={{
                alignItems: 'center',
                color: 'text.secondary',
                display: 'flex',
                flexShrink: 0,
                pr: 1,
              }}
            >
              <IconSearch />
            </Box>
          ) : null}
          <ControlledLocationAutocomplete
            clearOnSelect
            closeOnBlur={!isMobile}
            control={control}
            fullWidth
            name="location"
            navigate={navigate}
            onLocationChange={handleLocationChange}
            placeholder={placeholder}
            preloadPage="SearchPage"
            searchAppearance
          />
        </Box>
      </Box>
    </Box>
  );
};

TopbarSearchFormComponent.displayName = 'TopbarSearchForm';

const TopbarSearchForm = memo(TopbarSearchFormComponent);

export default TopbarSearchForm;
